import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { signupSendOTP, signupVerifyOTP } from "../ServiceAPI/login-register/api";
import { getStates } from "../ServiceAPI/master/api";
import { Eye, EyeOff, X } from "lucide-react";

const SignupModal = ({ isOpen, onClose, onSignupSuccess, openLoginModal, isDark }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    DOB: "",
    State: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [step, setStep] = useState("form"); // "form" | "otp"
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [contact, setContact] = useState("");
  const [otpSentTo, setOtpSentTo] = useState("");
  const [maskedContact, setMaskedContact] = useState("");

  // Fetch states when modal opens
  useEffect(() => {
    if (isOpen) {
      async function fetchStates() {
        try {
          const data = await getStates();
          console.log("States API response:", data);
          let stateArray = [];
          if (Array.isArray(data)) {
            stateArray = data;
          } else if (data?.data && Array.isArray(data.data)) {
            stateArray = data.data;
          }
          setStates(stateArray);
        } catch (err) {
          console.error("Failed to fetch states:", err);
          setStates([]);
        }
      }
      fetchStates();
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "DOB") {
      let val = value.replace(/\D/g, "");
      let formatted = val;
      if (val.length > 4) {
        formatted = `${val.slice(0, 2)}-${val.slice(2, 4)}-${val.slice(4, 8)}`;
      } else if (val.length > 2) {
        formatted = `${val.slice(0, 2)}-${val.slice(2)}`;
      }
      setForm({ ...form, [name]: formatted });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleReset = () => {
    setForm({
      name: "",
      email: "",
      phone: "",
      DOB: "",
      State: "",
    });
    setError("");
    setStep("form");
    setOtp("");
    setOtpError("");
  };

  // Step 1: Send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Validate required fields
      if (!form.name || !form.State) {
        setError("Name and State are required");
        setLoading(false);
        return;
      }

      // At least one of email or phone must be provided
      if (!form.email && !form.phone) {
        setError("Please provide Email or Phone number");
        setLoading(false);
        return;
      }

      const payload = {
        Username: form.name,
        Email: form.email || undefined,
        Phone: form.phone || undefined,
        State: form.State,
        DOB: form.DOB || undefined,
      };

      const response = await signupSendOTP(payload);

      if (response?.success) {
        setContact(response.contact);
        setOtpSentTo(response.otp_sent_to);
        setMaskedContact(response.masked_contact);
        setStep("otp");
        toast.success("OTP sent successfully!", {
          position: "top-right",
          autoClose: 2000,
        });
      } else {
        setError(response?.error || "Failed to send OTP");
      }
    } catch (err) {
      setError(err.message || "Failed to send OTP. Please try again.");
      console.error("Send OTP error:", err);
    }
    setLoading(false);
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setOtpError("");
    setLoading(true);

    try {
      if (!otp || otp.trim().length === 0) {
        setOtpError("Please enter the OTP");
        setLoading(false);
        return;
      }

      const response = await signupVerifyOTP(contact, otp);

      if (response?.success && response?.token) {
        // Store JWT token
        localStorage.setItem("token", response.token);

        // Store user info
        if (response.user) {
          localStorage.setItem("user", JSON.stringify(response.user));
        }

        toast.success("Signup successful! You are logged in.", {
          position: "top-right",
          autoClose: 2000,
        });

        // Close signup modal
        onClose();

        // Trigger callback
        if (onSignupSuccess) {
          onSignupSuccess();
        }

        // Reset form
        handleReset();
      } else {
        setOtpError(response?.error || "OTP verification failed");
      }
    } catch (err) {
      setOtpError(err.message || "OTP verification failed. Please try again.");
      console.error("Verify OTP error:", err);
    }
    setLoading(false);
  };

  const handleBackToForm = () => {
    setStep("form");
    setOtp("");
    setOtpError("");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div
        className={`${
          isDark ? "bg-gray-900" : "bg-white"
        } premium-card max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col`}
      >
        <div
          className={`flex items-center p-6 border-b ${
            isDark
              ? "bg-gray-800/50 border-gray-700"
              : "bg-blue-50/50 border-blue-100"
          }`}
        >
          <h2
            className={`text-2xl font-bold text-center flex-1 ${
              isDark ? "text-gray-100" : "text-gray-800"
            }`}
          >
            {step === "form" ? "Join RTO Bharat" : "Verify OTP"}
          </h2>
          <button
            onClick={onClose}
            className={`${
              isDark
                ? "text-gray-300 hover:text-gray-100 hover:bg-gray-700"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            } transition-colors p-2 rounded-full`}
          >
            <X size={24} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto">
          {step === "form" ? (
            // SIGNUP FORM STEP
            <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr]">
              {/* Left Side - Form */}
              <div className="p-8 lg:p-12">
                <div className="mb-10 text-center lg:text-left">
                  <h3
                    className={`text-3xl font-bold ${
                      isDark ? "text-white" : "text-gray-900"
                    } mb-3`}
                  >
                    Create Account
                  </h3>
                  <p
                    className={`${
                      isDark ? "text-gray-400" : "text-gray-500"
                    } text-lg`}
                  >
                    Experience the future of RTO services
                  </p>
                </div>

                {error && (
                  <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSendOTP} className="space-y-4">
                  {/* Two column grid for form fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Name */}
                    <div>
                      <label htmlFor="name" className="premium-label">
                        Full Name
                      </label>
                      <input
                        id="name"
                        type="text"
                        name="name"
                        placeholder="John Doe"
                        value={form.name}
                        onChange={handleChange}
                        required
                        className="premium-input"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="premium-label">
                        Email Address (Optional)
                      </label>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        placeholder="john@example.com"
                        value={form.email}
                        onChange={handleChange}
                        className="premium-input"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label htmlFor="phone" className="premium-label">
                        Phone Number (Optional)
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        name="phone"
                        placeholder="9876543210"
                        value={form.phone}
                        onChange={handleChange}
                        className="premium-input"
                      />
                    </div>

                    {/* State */}
                    <div className="relative">
                      <label htmlFor="State" className="premium-label">
                        State *
                      </label>
                      <select
                        id="State"
                        name="State"
                        value={form.State}
                        onChange={handleChange}
                        required
                        className="premium-input appearance-none pr-10"
                      >
                        <option value="">Select State</option>
                        {states.length === 0 ? (
                          <option disabled>Loading...</option>
                        ) : (
                          states.map((state, idx) => (
                            <option
                              key={state.Id || state.id || idx}
                              value={state.Id || state.id || state}
                            >
                              {state.Name || state.name || state}
                            </option>
                          ))
                        )}
                      </select>
                      <div className="absolute right-4 bottom-4 pointer-events-none text-gray-400">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>

                    {/* Date of Birth */}
                    <div className="col-span-2 sm:col-span-1">
                      <label htmlFor="DOB" className="premium-label">
                        Date of Birth (Optional)
                      </label>
                      <div className="relative">
                        <input
                          id="DOB"
                          type="text"
                          name="DOB"
                          placeholder="DD-MM-YYYY"
                          value={form.DOB}
                          onChange={handleChange}
                          maxLength={10}
                          className="premium-input"
                        />
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"} mt-2`}>
                    * Email or Phone is required to receive OTP
                  </p>

                  <div className="flex flex-col sm:flex-row gap-4 pt-6">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold py-4 px-6 rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transform transition-all active:scale-95 disabled:opacity-50 disabled:grayscale"
                    >
                      {loading ? "Sending OTP..." : "Send OTP"}
                    </button>
                    <button
                      type="button"
                      onClick={handleReset}
                      className={`flex-1 ${
                        isDark
                          ? "bg-gray-800 text-gray-300"
                          : "bg-gray-100 text-gray-600"
                      } font-bold py-4 px-6 rounded-xl hover:bg-gray-200 transition-all`}
                    >
                      Reset Form
                    </button>
                  </div>

                  <div className="text-center">
                    <p
                      className={`text-sm ${
                        isDark ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      Already have an account?{" "}
                      <button
                        type="button"
                        onClick={() => {
                          onClose();
                          if (openLoginModal) openLoginModal();
                        }}
                        className="text-green-600 hover:text-green-800 font-medium transition-colors duration-200 underline"
                      >
                        Sign In
                      </button>
                    </p>
                  </div>
                </form>
              </div>

              {/* Right Side - Information Panel */}
              <div className="bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 p-12 flex flex-col justify-center text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-400/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>

                <div className="relative space-y-10">
                  <div>
                    <h4 className="text-2xl font-bold mb-4">Why Choose RTO Bharat?</h4>
                    <p className="text-blue-100 text-lg leading-relaxed opacity-90">
                      Experience a seamless journey for all your vehicle-related
                      administrative needs. Fast, secure, and completely digital.
                    </p>
                  </div>

                  {/* Benefits */}
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Why Choose Us?</h4>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                      <ul className="space-y-1 text-xs text-green-100">
                        <li>• Quick and easy vehicle registration</li>
                        <li>• Digital license management</li>
                        <li>• 24/7 customer support</li>
                        <li>• Secure document storage</li>
                        <li>• Real-time status updates</li>
                        <li>• Government-approved processes</li>
                      </ul>
                    </div>
                  </div>

                  {/* Support */}
                  <div className="border-t border-white/20 pt-4">
                    <h4 className="text-base font-semibold mb-1">Need Assistance?</h4>
                    <p className="text-green-100 text-xs">
                      Our support team is here to help<br />
                      Email: support@rto.gov.in<br />
                      Phone: 1800-XXX-XXXX
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // OTP VERIFICATION STEP
            <div className="p-8 lg:p-12 max-w-md mx-auto">
              <div className="mb-8">
                <h3
                  className={`text-2xl font-bold ${
                    isDark ? "text-white" : "text-gray-900"
                  } mb-2`}
                >
                  Enter OTP
                </h3>
                <p
                  className={`${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  We sent an OTP to <strong>{maskedContact}</strong> via{" "}
                  <strong>{otpSentTo}</strong>
                </p>
              </div>

              {otpError && (
                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                  {otpError}
                </div>
              )}

              <form onSubmit={handleVerifyOTP} className="space-y-4">
                <div>
                  <label htmlFor="otp" className="premium-label">
                    OTP (6 digits)
                  </label>
                  <input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    placeholder="000000"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    maxLength={6}
                    className="premium-input text-center text-2xl tracking-widest font-mono"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold py-4 px-6 rounded-xl hover:shadow-lg hover:shadow-green-500/30 transform transition-all active:scale-95 disabled:opacity-50 disabled:grayscale"
                >
                  {loading ? "Verifying..." : "Verify OTP"}
                </button>

                <button
                  type="button"
                  onClick={handleBackToForm}
                  disabled={loading}
                  className={`w-full ${
                    isDark
                      ? "bg-gray-800 text-gray-300 hover:bg-gray-700"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  } font-bold py-3 px-6 rounded-xl transition-all`}
                >
                  Back to Form
                </button>

                <p className={`text-center text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  Didn't receive OTP?{" "}
                  <button
                    type="button"
                    onClick={handleBackToForm}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Request again
                  </button>
                </p>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignupModal;
